/**
 * When does a queued email actually go out?
 *
 * The clock starts the instant the lead arrives, and the campaign's delay is
 * applied from there. This module is the only place that turns "delay settings"
 * into a timestamp, so the browser and the Worker can never disagree.
 */

import type { Campaign } from './types'



const MINUTE_MS = 60 * 1000

/** Hard ceiling on any delay: 30 days, in minutes. */
export const MAX_DELAY_MINUTES = 30 * 24 * 60

/**
 * Floor on a delay that is switched ON, in minutes.
 *
 * Turning the delay on is a statement that mail should NOT go out the moment a
 * lead lands. A one-minute delay does not express that — it just adds a tick of
 * jitter — so anything under this is rejected at the form and clamped here, and
 * "I want it to go now" is spelled delay_enabled = false instead.
 */
export const MIN_DELAY_MINUTES = 5

type DelayFields = Partial<
  Pick<
    Campaign,
    'delay_enabled' | 'delay_mode' | 'delay_hours' | 'delay_minutes' | 'random_window_minutes'
  >
>

/**
 * Is the delay switched on?
 *
 * When the column is missing — 0018 not applied yet, or a caller that didn't
 * select it — fall back to the SAME rule 0018 backfills with: a campaign whose
 * delay is zero was saying "send immediately" in the only vocabulary it had.
 *
 * Reading a missing switch as plain `true` would be wrong in a way that is hard
 * to see: it would retro-apply the new 5-minute floor to every existing
 * zero-delay campaign and stall mail that used to go out on the next tick.
 * Matching the backfill keeps behaviour identical either side of the migration.
 */
export function delayEnabled(c: DelayFields): boolean {
  if (typeof c.delay_enabled === 'boolean') return c.delay_enabled
  if (c.delay_mode === 'random_1h') return randomWindowMinutes(c) > 0
  return fixedDelayMinutes(c) > 0
}

/**
 * The fixed delay in minutes. Falls back to the legacy hours column when
 * delay_minutes has not been backfilled, so old rows keep their timing.
 */
export function fixedDelayMinutes(c: DelayFields): number {
  if (c.delay_minutes != null && Number.isFinite(Number(c.delay_minutes))) {
    return Math.max(0, Math.round(Number(c.delay_minutes)))
  }
  return Math.max(0, Math.round(Number(c.delay_hours ?? 0) * 60))
}

/** Upper bound of the random window, in minutes. Defaults to one hour. */
export function randomWindowMinutes(c: DelayFields): number {
  const n = Number(c.random_window_minutes)
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 60
}

export function computeScheduledAt(
  campaign: DelayFields,
  from: Date = new Date(),
  random: () => number = Math.random,
): Date {
  // Delay off: due immediately. The next tick (<= 60s) picks it up — there is no
  // approval gate and nothing else to wait for.
  if (!delayEnabled(campaign)) return new Date(from.getTime())

  if (campaign.delay_mode === 'random_1h') {
    // Uniformly random inside the configured window, but never sooner than the
    // floor — so a 5..60 window rolls in [5, 60], not [0, 60].
    const window = Math.max(MIN_DELAY_MINUTES, randomWindowMinutes(campaign))
    const span = window - MIN_DELAY_MINUTES
    const minutes = MIN_DELAY_MINUTES + random() * span
    return new Date(from.getTime() + Math.floor(minutes * MINUTE_MS))
  }

  const minutes = Math.max(MIN_DELAY_MINUTES, fixedDelayMinutes(campaign))
  return new Date(from.getTime() + minutes * MINUTE_MS)
}

/**
 * Why this delay is not allowed, or null when it is fine. Shared by the form and
 * the save path so the browser cannot talk the server into a shorter delay than
 * the control permits.
 */
export function validateDelay(c: DelayFields): string | null {
  if (!delayEnabled(c)) return null

  const isRandom = c.delay_mode === 'random_1h'
  const minutes = isRandom ? randomWindowMinutes(c) : fixedDelayMinutes(c)
  const label = isRandom ? 'window' : 'delay'

  if (minutes < MIN_DELAY_MINUTES) {
    return `Minimum ${label} is ${MIN_DELAY_MINUTES} minutes. Turn Delay off to send as soon as each lead is added.`
  }
  if (minutes > MAX_DELAY_MINUTES) {
    return `Maximum ${label} is 30 days (${MAX_DELAY_MINUTES} minutes).`
  }
  return null
}

/** "45 minutes", "2 hours", "1 hour 30 minutes" */
export function formatMinutes(total: number): string {
  const m = Math.max(0, Math.round(total))
  if (m === 0) return '0 minutes'
  const h = Math.floor(m / 60)
  const rem = m % 60
  const parts: string[] = []
  if (h) parts.push(`${h} hour${h === 1 ? '' : 's'}`)
  if (rem) parts.push(`${rem} minute${rem === 1 ? '' : 's'}`)
  return parts.join(' ')
}

/** Short label for the campaign list. */
export function describeDelay(campaign: DelayFields): string {
  const clock = 'after the lead arrives'
  if (!delayEnabled(campaign)) return 'No delay — sends immediately'
  if (campaign.delay_mode === 'random_1h') {
    return `Random, up to ${formatMinutes(randomWindowMinutes(campaign))} ${clock}`
  }
  return `${formatMinutes(Math.max(MIN_DELAY_MINUTES, fixedDelayMinutes(campaign)))} ${clock}`
}

/** Plain-words line shown under the delay control. */
export function explainDelay(campaign: DelayFields): string {
  if (!delayEnabled(campaign)) {
    return 'Every lead is queued as due straight away — mail goes out on the next processing tick, within a minute.'
  }
  if (campaign.delay_mode === 'random_1h') {
    return `Each lead sends at a random time between ${formatMinutes(
      MIN_DELAY_MINUTES,
    )} and ${formatMinutes(
      Math.max(MIN_DELAY_MINUTES, randomWindowMinutes(campaign)),
    )} after being added.`
  }
  return `Each lead sends ~${formatMinutes(
    Math.max(MIN_DELAY_MINUTES, fixedDelayMinutes(campaign)),
  )} after being added.`
}

/**
 * Minutes since local midnight in the given IANA timezone. Uses Intl rather
 * than date maths so DST is handled by the platform.
 */
export function minutesOfDayIn(tz: string | null | undefined, now: Date = new Date()): number {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz || 'UTC',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(now)
    const h = Number(parts.find((p) => p.type === 'hour')?.value ?? '0')
    const m = Number(parts.find((p) => p.type === 'minute')?.value ?? '0')
    return h * 60 + m
  } catch {
    // Unknown timezone — fall back to UTC rather than blocking every send.
    return now.getUTCHours() * 60 + now.getUTCMinutes()
  }
}

/**
 * Is a campaign allowed to send right now?
 *
 * Three gates, in order: it must not be waiting for its scheduled start, and
 * if a daily window is set the current local time must fall inside it. A
 * window that wraps midnight (e.g. 22:00–06:00) is supported.
 */
export function canSendNow(
  campaign: Pick<
    Campaign,
    'starts_at' | 'window_enabled' | 'window_start_min' | 'window_end_min' | 'send_tz'
  >,
  now: Date = new Date(),
): { ok: boolean; reason?: string } {
  if (campaign.starts_at && new Date(campaign.starts_at).getTime() > now.getTime()) {
    return { ok: false, reason: `waiting for scheduled start ${campaign.starts_at}` }
  }

  if (!campaign.window_enabled) return { ok: true }

  const cur = minutesOfDayIn(campaign.send_tz, now)
  const start = campaign.window_start_min
  const end = campaign.window_end_min
  const inside = start <= end ? cur >= start && cur < end : cur >= start || cur < end

  return inside
    ? { ok: true }
    : { ok: false, reason: `outside the daily send window ${fmtMin(start)}–${fmtMin(end)}` }
}

function fmtMin(m: number): string {
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

/** Midnight UTC today — the boundary the daily cap is measured against. */
export function startOfUtcDay(now: Date = new Date()): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
}

/** Exponential-ish backoff between delivery attempts: 5m, 15m, 45m. */
export function retryDelayMs(attempts: number): number {
  return Math.min(45, 5 * Math.pow(3, Math.max(0, attempts - 1))) * 60 * 1000
}
